// App.jsx — Root component
// NavBar and Footer now show on ALL pages including login,
// so the login page feels part of the same site.

import { useState, useEffect, useCallback } from "react";
import { useTheme } from "./context/ThemeContext";
import { useAuth  } from "./context/AuthContext";
import * as api from "./services/api";
import { SpeedInsights } from "@vercel/speed-insights/react";
import { Analytics } from "@vercel/analytics/react";

import NavBar      from "./components/NavBar";
import Footer      from "./components/Footer";
import AuthPage    from "./pages/AuthPage";
import ArchivePage from "./pages/ArchivePage";
import ProblemPage from "./pages/ProblemPage";
import ContactPage from "./pages/ContactPage";
import AdminPage   from "./pages/AdminPage";
import PotdPage    from "./pages/PotdPage";
import LeaderboardPage from "./pages/LeaderboardPage";
import ProfilePage from "./pages/ProfilePage";
import InfoPage    from "./pages/InfoPage";

export default function App() {
  const { t }                          = useTheme();
  const { user, loading: authLoading } = useAuth();

  const initialProblemId = window.location.pathname.match(/^\/(?:app\/)?problem\/([^/]+)\/?$/)?.[1];
  const initialPage = initialProblemId
    ? "problem"
    : ["/potd", "/leaderboard", "/profile"].includes(window.location.pathname.replace(/\/$/, ""))
      ? window.location.pathname.replace(/\/$/, "").slice(1)
      : ["/privacy", "/terms", "/contact", "/about"].includes(window.location.pathname.replace(/\/$/, ""))
        ? window.location.pathname.replace(/\/$/, "").slice(1)
      : "home";
  const [page,    setPage]    = useState(initialPage);
  const [problem, setProblem] = useState(null);
  const [problemLoading, setProblemLoading] = useState(Boolean(initialProblemId));
  const [problemError, setProblemError] = useState("");
  const [problems,     setProblems]     = useState([]);
  const [problemsError, setProblemsError] = useState("");
  const [problemsMeta, setProblemsMeta] = useState({ total: 0, pages: 1, page: 1 });
  const [proLoading,   setProLoading]   = useState(true);
  const [filters,      setFilters]      = useState({ page: 1, topic: "All", difficulty: "All", firm: "All", search: "" });

  const nav = (nextPage, problemId) => {
    const path = nextPage === "problem" && problemId
      ? `/problem/${encodeURIComponent(problemId)}`
      : nextPage === "home" ? "/" : `/${nextPage}`;
    window.history.pushState({}, "", path);
    setPage(nextPage);
    if (nextPage !== "problem") {
      setProblem(null);
      setProblemError("");
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const fetchProblems = useCallback(async (f) => {
    setProLoading(true);
    setProblemsError("");
    try {
      const data = await api.getProblems({ ...f, limit: 20 });
      setProblems(data.problems.map(normalise));
      setProblemsMeta({ total: data.total, pages: data.pages, page: data.page });
    } catch (err) {
      console.warn("Problems API unavailable:", err.message);
      setProblemsError(err.message || "Unable to load problems.");
    } finally {
      setProLoading(false);
    }
  }, []);

  useEffect(() => {
    const timer = window.setTimeout(() => fetchProblems(filters), 250);
    return () => window.clearTimeout(timer);
  }, [filters, fetchProblems]);

  const loadProblem = useCallback(async (problemId) => {
    setProblemLoading(true);
    setProblemError("");
    try {
      const data = await api.getProblem(decodeURIComponent(problemId));
      setProblem(normalise(data.problem));
      setPage("problem");
    } catch (err) {
      setProblem(null);
      setProblemError(err.message);
    } finally {
      setProblemLoading(false);
    }
  }, []); // eslint-disable-line

  function normalise(p) {
    return {
      ...p,
      id:          p.problemId || p.id,
      askedIn:     p.askedIn   || [],
      tags:        p.tags      || [],
      dateAdded:   p.dateAdded || new Date().toISOString().split("T")[0],
      ratingCount: p.ratingCount || 0,
    };
  }

  useEffect(() => {
    if (initialProblemId) loadProblem(initialProblemId);

    const handleHistoryChange = () => {
      const problemId = window.location.pathname.match(/^\/(?:app\/)?problem\/([^/]+)\/?$/)?.[1];
      if (problemId) {
        loadProblem(problemId);
      } else {
        setProblem(null);
        setProblemError("");
        const route = window.location.pathname.replace(/\/$/, "").slice(1);
        setPage(["privacy", "terms", "contact", "about", "potd", "leaderboard", "profile"].includes(route) ? route : "home");
      }
    };

    window.addEventListener("popstate", handleHistoryChange);
    return () => window.removeEventListener("popstate", handleHistoryChange);
  }, []); // eslint-disable-line
  const handleRatingSubmit = async (problemId, difficulty, clarity) => {
    try {
      const { problem: updated } = await api.rateProblem(problemId, difficulty, clarity);
      setProblems((ps) => ps.map((p) => p.id === problemId ? normalise(updated) : p));
      if (problem?.id === problemId) setProblem(normalise(updated));
    } catch (err) {
      console.error("Rating failed:", err.message);
    }
  };

  // Loading spinner while JWT is being verified on mount
  if (authLoading) {
    return (
      <div style={{ minHeight: "100vh", background: t.bg, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ width: 32, height: 32, border: `3px solid ${t.border}`, borderTopColor: t.text, borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column", background: t.bg, color: t.text, fontFamily: "'Inter',system-ui,sans-serif", transition: "background 0.25s, color 0.25s" }}>
      <SpeedInsights />
      <Analytics />

      {/* NavBar shown on ALL pages — login page is part of the site */}
      <NavBar page={page} nav={nav} />

      <main style={{ flex: 1 }}>
        {page === "login" && <AuthPage nav={nav} />}

        {page === "home" && (
          <ArchivePage
            problems={problems}
            meta={problemsMeta}
            loading={proLoading}
            error={problemsError}
            onRetry={() => fetchProblems(filters)}
            filters={filters}
            setFilters={setFilters}
            nav={nav}
            setProblem={setProblem}
          />
        )}

        {page === "problem" && problemLoading && (
          <div style={{ maxWidth: 860, margin: "0 auto", padding: "160px 32px", textAlign: "center", color: t.text3 }}>Loading problem...</div>
        )}

        {page === "problem" && problemError && !problemLoading && (
          <div style={{ maxWidth: 860, margin: "0 auto", padding: "160px 32px", textAlign: "center", color: t.text3 }}>
            <p>{problemError}</p>
            <button onClick={() => nav("home")} style={{ background: t.primary, color: t.primaryFg, border: "none", borderRadius: 8, padding: "10px 18px", cursor: "pointer" }}>Back to Archive</button>
          </div>
        )}

        {page === "problem" && problem && !problemLoading && (
          <ProblemPage
            p={problem}
            onBack={() => nav("home")}
            nav={nav}
            onRatingSubmit={handleRatingSubmit}
          />
        )}

        {page === "contact" && <ContactPage />}
        {["privacy", "terms", "about"].includes(page) && <InfoPage page={page} nav={nav} />}

        {page === "potd" && <PotdPage />}
        {page === "leaderboard" && <LeaderboardPage />}
        {page === "profile" && <ProfilePage nav={nav} />}

        {page === "admin" && user?.role === "admin" && (
          <AdminPage onRefresh={() => fetchProblems(filters)} />
        )}
        {page === "admin" && user?.role !== "admin" && (
          <div style={{ textAlign: "center", padding: "160px 0", color: t.text3 }}>
            <p>Access denied.</p>
          </div>
        )}
      </main>

      <Footer nav={nav} />
    </div>
  );
}
